import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Button,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { Link } from "react-router-dom";
import mainStyles from "styles";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { GetCoursesResponse } from "@safetyculture/s12-apis-connect-web/s12/training/courses/v1/responses_pb";

import { TrainingCoursesParams, getCourses } from "api/endpoints/training";
import { callOptionsHeaders } from "api/helpers";
import RPCTablePaginationActions from "components/shared/table/rpc-table-pagination-actions";
import { PermissionsContext } from "context/permissions-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

enum MatchType {
  EXACT = 1,
  PREFIX = 2,
  WHOLE_WORD = 3,
  SUBSTRING = 4,
  NO_MATCH = 5,
}

const getMatchType = (title: string, query: string): MatchType => {
  if (!query) return MatchType.NO_MATCH;

  const lowerTitle = title.toLowerCase();
  const lowerQuery = query.toLowerCase();

  if (lowerTitle === lowerQuery) {
    return MatchType.EXACT;
  }

  if (lowerTitle.startsWith(lowerQuery)) {
    return MatchType.PREFIX;
  }

  const wordBoundaryRegex = new RegExp(
    `\\b${lowerQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
  );
  if (wordBoundaryRegex.test(lowerTitle)) {
    return MatchType.WHOLE_WORD;
  }

  if (lowerTitle.includes(lowerQuery)) {
    return MatchType.SUBSTRING;
  }

  return MatchType.NO_MATCH;
};

interface OrgTrainingCoursesTabProps {
  roleId: string;
  regionCode: string;
  canCopyCourse: boolean;
  showError: (error: GrpcError) => void;
}

export const OrgTrainingCoursesTab = ({
  roleId,
  regionCode,
  canCopyCourse,
  showError,
}: OrgTrainingCoursesTabProps) => {
  const classes = mainStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [courses, setCourses] = useState<GetCoursesResponse>(
    new GetCoursesResponse(),
  );
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const pageSize = 10;
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(value);
    }, 300);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    getOrganisationCourseList(roleId, pageSize, newPage, debouncedSearchQuery);
  };

  const getOrganisationCourseList = useCallback(
    (
      roleId: string,
      pageSize: number,
      pageIndex: number,
      searchQuery?: string,
    ) => {
      const payload: TrainingCoursesParams = {
        organisationID: roleId,
        pageSize,
        page: pageIndex + 1,
        searchQuery: searchQuery || undefined,
      };

      setIsLoading(true);
      getCourses(payload, callOptionsHeaders(regionCode, roleId))
        .then((res) => {
          if (searchQuery && res.items.length > 0) {
            const sortedItems = [...res.items].sort((a, b) => {
              const matchTypeA = getMatchType(a.title, searchQuery);
              const matchTypeB = getMatchType(b.title, searchQuery);
              return matchTypeA - matchTypeB;
            });
            res.items = sortedItems;
          }
          setCourses(res);
        })
        .catch((e) => {
          showError(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [regionCode, showError],
  );

  useEffect(() => {
    setPage(0);
    getOrganisationCourseList(roleId, pageSize, 0, debouncedSearchQuery);
  }, [roleId, debouncedSearchQuery, getOrganisationCourseList]);

  return (
    <Grid container sx={classes.paddingTopSmall}>
      <Grid item xs={12} sx={{ marginBottom: 2 }}>
        <TextField
          id="course-search-input"
          label="Search by course title"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Type to search..."
          sx={{ width: 300 }}
          inputProps={{ "data-anchor": "course-search-input" }}
        />
      </Grid>
      <TableContainer component={Paper}>
        <Table aria-label="courses-list-table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: "30%", paddingLeft: "30px" }}>
                Title
              </TableCell>
              <TableCell style={{ width: "50%" }}>Description</TableCell>
              <TableCell style={{ width: "20%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : courses.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <b>No courses found.</b>
                </TableCell>
              </TableRow>
            ) : (
              courses.items.map((course) => (
                <TableRow key={course.id}>
                  <TableCell style={{ paddingLeft: "30px" }}>
                    {course.title}
                  </TableCell>
                  <TableCell>{course.description}</TableCell>
                  <TableCell>
                    {canCopyCourse && (
                      <Link
                        to={{
                          pathname: "/copy-course",
                          state: {
                            sourceOrgId: roleId,
                            sourceRegionCode: regionCode,
                            course,
                          },
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                        >
                          Copy Course
                        </Button>
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[pageSize]}
                count={courses.totalCount}
                rowsPerPage={pageSize}
                page={page}
                component="td"
                onPageChange={handleChangePage}
                ActionsComponent={RPCTablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Grid>
  );
};

interface OrgTrainingCoursesTabWrapperProps {
  roleId: string;
  regionCode: string;
}

const OrgTrainingCoursesTabWrapper = ({
  roleId,
  regionCode,
}: OrgTrainingCoursesTabWrapperProps) => {
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const { showError } = useContext(SnackbarContext);
  const canCopyCourse =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.COPY_COURSE]);

  return (
    <OrgTrainingCoursesTab
      roleId={roleId}
      regionCode={regionCode}
      canCopyCourse={canCopyCourse}
      showError={showError}
    />
  );
};

export default OrgTrainingCoursesTabWrapper;
